import React, { useMemo, useState } from "react";
import {
    HardDrive, Zap, Eye, EyeOff, ChevronDown, ChevronUp,
    CheckCircle2, XCircle, Loader2, X, Server, Info,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { Section, Field, RadioPill } from "@/components/storage/ConnectModalPrimitives";

/**
 * ConnectStorageModal — "Synology-simple" NAS connection UX.
 *
 * Flow:  Paste smb://host/share  OR  fill 4 fields  →  Test Connection  →  Save
 * Advanced options are collapsed by default. Credentials are encrypted at rest
 * server-side — the plaintext password never leaves this form after Save.
 *
 * Pass `editAgent` (an existing NAS agent doc) to edit its connection in
 * place instead of creating a new one — fields pre-fill from it, and Save
 * PATCHes /agents/{id}/storage. The password field is never pre-filled
 * (the server never sends it back); leaving it blank on save keeps the
 * currently stored credential unchanged.
 */
export default function ConnectStorageModal({ role = "source", editAgent = null, onClose, onSaved }) {
    const isEdit = !!editAgent;
    const initialNas = editAgent?.nas || {};
    const hadPassword = !!initialNas.has_password;

    const [name, setName] = useState(editAgent?.name || "");
    const [protocol, setProtocol] = useState(initialNas.protocol || "smb");
    const [host, setHost] = useState(initialNas.host || "");
    const [share, setShare] = useState(initialNas.share || "");
    const [port, setPort] = useState(initialNas.port ? String(initialNas.port) : ""); // "" → auto
    const [username, setUsername] = useState(initialNas.username || "");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);

    const [quick, setQuick] = useState("");
    // Best-effort auto-detect (Test Connection) can only ever go by
    // hostname substrings — a bare IP like 10.10.20.5 never matches
    // anything, which is the common case for real NAS boxes. Letting the
    // operator pick the brand explicitly is what makes the Devices page's
    // per-vendor NAS tabs actually work for those. Pre-fill from whatever
    // vendor_hint is already stored, recognizing it even if it came back
    // as "Synology NAS" rather than the bare "Synology" this list uses.
    const [vendorOverride, setVendorOverride] = useState(() => {
        const v = (initialNas.vendor_hint || "").toLowerCase();
        if (v.includes("synology")) return "Synology";
        if (v.includes("qnap")) return "QNAP";
        if (v.includes("netapp")) return "NetApp";
        if (v.includes("truenas") || v.includes("freenas")) return "TrueNAS";
        if (v.includes("ubiquiti") || v.includes("unifi")) return "Ubiquiti";
        if (v.includes("ugreen")) return "UGreen";
        return v ? "Other" : "";
    });

    const [showAdv, setShowAdv] = useState(false);
    const [mountMode, setMountMode] = useState(initialNas.mount_mode || "rw");
    const [timeoutSecs, setTimeoutSecs] = useState(initialNas.timeout_secs || 30);
    const [bandwidth, setBandwidth] = useState(initialNas.bandwidth_limit_mbps || 0);
    const [smbVersion, setSmbVersion] = useState(initialNas.smb_version || "auto");
    const [nfsVersion, setNfsVersion] = useState(initialNas.nfs_version || "auto");
    const [mountOptions, setMountOptions] = useState(initialNas.options || "");

    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // {ok, message, display_path, vendor, resolved_ip}
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const defaultPort = protocol === "smb" ? 445 : 2049;
    const portPlaceholder = `Auto (${defaultPort})`;

    const sharePlaceholder = protocol === "smb"
        ? "backups   or   \\\\host\\backups"
        : "/volume1/data";

    // ---- Quick Connect parser ----
    const applyQuick = () => {
        const raw = quick.trim();
        if (!raw) return;
        // Accept:  smb://user:pw@host:port/share
        //          nfs://host:/export
        //          \\host\share
        //          host:/export
        try {
            let proto = null, h = null, sh = null, p = null, u = null, pw = null;

            // UNC: \\host\share
            const unc = raw.match(/^\\\\([^\\/]+)\\(.+)$/);
            if (unc) {
                proto = "smb"; h = unc[1]; sh = unc[2];
            } else if (/^[a-z]+:\/\//i.test(raw)) {
                const url = new URL(raw);
                proto = url.protocol.replace(":", "").toLowerCase();
                if (proto !== "smb" && proto !== "nfs" && proto !== "cifs") {
                    throw new Error("Only smb:// or nfs:// URLs are supported");
                }
                if (proto === "cifs") proto = "smb";
                h = url.hostname;
                p = url.port || "";
                u = decodeURIComponent(url.username || "");
                pw = decodeURIComponent(url.password || "");
                sh = decodeURIComponent(url.pathname || "").replace(/^\//, "");
                // NFS path must keep a leading slash for display parity
                if (proto === "nfs" && sh) sh = "/" + sh;
            } else if (raw.includes(":/")) {
                // host:/export
                const [hh, ex] = raw.split(":/");
                proto = "nfs"; h = hh; sh = "/" + ex;
            } else {
                throw new Error("Use smb://host/share, nfs://host:/export, or \\\\host\\share");
            }

            setProtocol(proto);
            setHost(h || "");
            setShare(sh || "");
            setPort(p || "");
            if (u) setUsername(u);
            if (pw) setPassword(pw);
            setTestResult(null);
            setErr("");
        } catch (e) {
            setErr(e.message || "Invalid quick-connect URL");
        }
    };

    // ---- Test connection ----
    const test = async () => {
        setErr("");
        if (!host || !share) {
            setErr("Host and shared folder/path are required to test");
            return;
        }
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await http.post("/agents/storage/test", {
                protocol, host, share,
                port: port ? Number(port) : null,
                timeout_secs: Math.min(Number(timeoutSecs) || 5, 15),
            });
            setTestResult(data);
        } catch (e) {
            setTestResult({ ok: false, message: errMsg(e) });
        } finally {
            setTesting(false);
        }
    };

    // ---- Save ----
    const displayName = useMemo(() => {
        if (name.trim()) return name.trim();
        const vendor = testResult?.vendor;
        if (vendor && host) return `${vendor.split(" ")[0]}-${host.split(".")[0]}`;
        if (host) return host.split(".")[0];
        return "";
    }, [name, host, testResult]);

    const save = async () => {
        setErr("");
        if (!host || !share) {
            setErr("Please fill host and shared folder/path");
            return;
        }
        if (!displayName) {
            setErr("Please name this storage");
            return;
        }
        setBusy(true);
        try {
            const nas = {
                protocol, host, share,
                port: port ? Number(port) : null,
                mount_point: initialNas.mount_point || "/mnt/moovbytes",
                username: username || null,
                // Blank on an edit means "keep the stored credential" — the
                // server never sends the real password back, so this field
                // only ever carries a REPLACEMENT the operator just typed.
                password: password || null,
                options: mountOptions || null,
                mount_mode: mountMode,
                timeout_secs: Number(timeoutSecs) || 30,
                bandwidth_limit_mbps: Number(bandwidth) || 0,
                smb_version: smbVersion,
                nfs_version: nfsVersion,
                vendor_hint: vendorOverride || testResult?.vendor || initialNas.vendor_hint || null,
            };
            if (isEdit) {
                const { data } = await http.patch(`/agents/${editAgent.id}/storage`, { name: displayName, nas });
                onSaved(data);
            } else {
                const { data } = await http.post("/agents", { name: displayName, role, kind: "nas", version: "1.0.0-nas", nas });
                onSaved(data);
            }
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
            <div
                className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl"
                data-testid="connect-storage-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#00C2FF] to-[#5b5ef0] text-white shadow-md">
                            <HardDrive size={16} />
                        </div>
                        <div>
                            <div className="font-display text-lg font-bold">
                                {isEdit ? "Edit Storage Connection" : "Connect Storage"}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {isEdit ? editAgent.name : (role === "source" ? "Source NAS" : "Destination NAS")}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border p-1.5 hover:border-foreground/40"
                        data-testid="connect-storage-close"
                        aria-label="Close"
                    >
                        <X size={13} />
                    </button>
                </div>

                <div className="space-y-4 p-5">
                    {/* Quick Connect */}
                    <div className="rounded-lg border border-dashed border-[#00C2FF]/50 bg-[#00C2FF]/5 p-3">
                        <label className="mb-1.5 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            <Zap size={11} className="text-[#00C2FF]" /> Quick Connect
                        </label>
                        <div className="flex gap-2">
                            <input
                                value={quick}
                                onChange={(e) => setQuick(e.target.value)}
                                placeholder="smb://192.168.1.10/data  or  nfs://10.0.0.5:/volume1/backups"
                                data-testid="storage-quick-input"
                                className="mb-focus flex-1 rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                            <button
                                type="button"
                                onClick={applyQuick}
                                disabled={!quick.trim()}
                                data-testid="storage-quick-apply"
                                className="rounded-md bg-[#00C2FF] px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#00a6dd] disabled:opacity-50"
                            >
                                Parse
                            </button>
                        </div>
                    </div>

                    {/* Protocol */}
                    <Section title="Protocol">
                        <div className="flex flex-wrap gap-2">
                            <RadioPill
                                active={protocol === "smb"}
                                onClick={() => { setProtocol("smb"); setTestResult(null); }}
                                label="SMB"
                                sub="Windows · Synology · QNAP"
                                testId="storage-proto-smb"
                            />
                            <RadioPill
                                active={protocol === "nfs"}
                                onClick={() => { setProtocol("nfs"); setTestResult(null); }}
                                label="NFS"
                                sub="Linux · NetApp · TrueNAS"
                                testId="storage-proto-nfs"
                            />
                        </div>
                    </Section>

                    {/* Connection */}
                    <Section title="Connection">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_110px]">
                            <Field label="Host / IP address" required>
                                <input
                                    value={host}
                                    onChange={(e) => { setHost(e.target.value); setTestResult(null); }}
                                    placeholder="192.168.1.10"
                                    data-testid="storage-host-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                            <Field label="Port">
                                <input
                                    value={port}
                                    onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder={portPlaceholder}
                                    data-testid="storage-port-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                        <div className="mt-3">
                            <Field label={protocol === "smb" ? "Shared folder" : "Export path"} required>
                                <input
                                    value={share}
                                    onChange={(e) => { setShare(e.target.value); setTestResult(null); }}
                                    placeholder={sharePlaceholder}
                                    data-testid="storage-share-input"
                                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </Field>
                        </div>
                    </Section>

                    {/* Brand — drives the Devices page's per-vendor NAS tabs.
                        Auto-detect only ever works off a hostname substring,
                        so a bare IP (the common real-world case) needs a
                        manual pick to be classified correctly. */}
                    <Section title="Brand">
                        <select
                            value={vendorOverride}
                            onChange={(e) => setVendorOverride(e.target.value)}
                            data-testid="storage-vendor-select"
                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                            <option value="">Auto-detect</option>
                            <option value="Synology">Synology</option>
                            <option value="QNAP">QNAP</option>
                            <option value="NetApp">NetApp</option>
                            <option value="TrueNAS">TrueNAS</option>
                            <option value="Ubiquiti">Ubiquiti (UniFi)</option>
                            <option value="UGreen">UGreen</option>
                            <option value="Other">Other</option>
                        </select>
                    </Section>

                    {/* Credentials (SMB only — NFS is typically uid-based) */}
                    {protocol === "smb" && (
                        <Section title="Credentials">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Field label="Username">
                                    <input
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="admin"
                                        autoComplete="username"
                                        data-testid="storage-username-input"
                                        className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    />
                                </Field>
                                <Field label="Password">
                                    <div className="relative">
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder={isEdit ? "Leave blank to keep current" : "••••••••"}
                                            autoComplete="new-password"
                                            data-testid="storage-password-input"
                                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 pr-9 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((s) => !s)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            data-testid="storage-password-toggle"
                                            tabIndex={-1}
                                        >
                                            {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                                        </button>
                                    </div>
                                    {isEdit && (
                                        <div className="mt-1 text-[10px] text-muted-foreground" data-testid="storage-password-edit-hint">
                                            {hadPassword
                                                ? "A password is already stored for this connection — only fill this in to replace it."
                                                : "No password is currently stored for this connection."}
                                        </div>
                                    )}
                                </Field>
                            </div>
                        </Section>
                    )}

                    {/* Advanced (collapsed) */}
                    <div className="rounded-lg border border-border bg-background/40">
                        <button
                            type="button"
                            onClick={() => setShowAdv((s) => !s)}
                            data-testid="storage-advanced-toggle"
                            className="flex w-full items-center justify-between px-4 py-2.5 text-left"
                        >
                            <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                Advanced Settings
                            </span>
                            {showAdv ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {showAdv && (
                            <div className="space-y-3 border-t border-border px-4 py-3">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <Field label="Mount mode">
                                        <select
                                            value={mountMode}
                                            onChange={(e) => setMountMode(e.target.value)}
                                            data-testid="storage-mode-input"
                                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                            <option value="rw">Read / Write</option>
                                            <option value="ro">Read only</option>
                                        </select>
                                    </Field>
                                    <Field label="Timeout (sec)">
                                        <input
                                            type="number"
                                            min={1}
                                            max={600}
                                            value={timeoutSecs}
                                            onChange={(e) => setTimeoutSecs(Number(e.target.value) || 30)}
                                            data-testid="storage-timeout-input"
                                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                    </Field>
                                    <Field label="Bandwidth limit (MB/s)">
                                        <input
                                            type="number"
                                            min={0}
                                            value={bandwidth}
                                            onChange={(e) => setBandwidth(Number(e.target.value) || 0)}
                                            placeholder="0 = unlimited"
                                            data-testid="storage-bandwidth-input"
                                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {protocol === "smb" ? (
                                        <Field label="SMB version">
                                            <select
                                                value={smbVersion}
                                                onChange={(e) => setSmbVersion(e.target.value)}
                                                data-testid="storage-smb-version"
                                                className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            >
                                                <option value="auto">Auto</option>
                                                <option value="3.1.1">3.1.1</option>
                                                <option value="3.0">3.0</option>
                                                <option value="2.1">2.1</option>
                                                <option value="1.0">1.0 (legacy)</option>
                                            </select>
                                        </Field>
                                    ) : (
                                        <Field label="NFS version">
                                            <select
                                                value={nfsVersion}
                                                onChange={(e) => setNfsVersion(e.target.value)}
                                                data-testid="storage-nfs-version"
                                                className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            >
                                                <option value="auto">Auto</option>
                                                <option value="4">v4</option>
                                                <option value="3">v3</option>
                                                <option value="2">v2</option>
                                            </select>
                                        </Field>
                                    )}
                                    <Field label="Mount options (optional)">
                                        <input
                                            value={mountOptions}
                                            onChange={(e) => setMountOptions(e.target.value)}
                                            placeholder={protocol === "smb" ? "sec=ntlmssp,nounix" : "hard,intr"}
                                            data-testid="storage-mount-options"
                                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Name (auto-filled after vendor detection) */}
                    <Section title="Display name">
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={displayName || "e.g. Primary Synology"}
                            data-testid="storage-name-input"
                            className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </Section>

                    {/* Test Connection result. `stage === "private-network"` is
                        a distinct third state — a LAN NAS address that the
                        cloud control plane structurally can't reach, which
                        isn't evidence anything is actually wrong (a worker
                        agent on the same network does the real connecting
                        later), so it renders as neutral info rather than the
                        same red failure as a genuine bad host/port/creds. */}
                    {testResult && (
                        <div
                            className={
                                "rounded-lg border p-3 " +
                                (testResult.ok
                                    ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5"
                                    : testResult.stage === "private-network"
                                    ? "border-blue-400/40 bg-blue-500/5"
                                    : "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5")
                            }
                            data-testid="storage-test-result"
                        >
                            <div className="flex items-start gap-2">
                                {testResult.ok ? (
                                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                                ) : testResult.stage === "private-network" ? (
                                    <Info size={16} className="mt-0.5 shrink-0 text-blue-400" />
                                ) : (
                                    <XCircle size={16} className="mt-0.5 shrink-0 text-[hsl(var(--destructive))]" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <div
                                        className={
                                            "text-sm font-semibold " +
                                            (testResult.ok
                                                ? "text-[hsl(var(--success))]"
                                                : testResult.stage === "private-network"
                                                ? "text-blue-300"
                                                : "text-[hsl(var(--destructive))]")
                                        }
                                    >
                                        {testResult.ok ? "Connected successfully" : testResult.message || "Unable to connect"}
                                    </div>
                                    {testResult.ok && (
                                        <div className="mt-1 space-y-0.5 font-mono text-[10px] text-muted-foreground">
                                            {testResult.vendor && (
                                                <div>
                                                    Detected: <span className="text-foreground">{testResult.vendor}</span>
                                                </div>
                                            )}
                                            <div>
                                                Path: <span className="text-foreground">{testResult.display_path}</span>
                                            </div>
                                            {testResult.resolved_ip && (
                                                <div>
                                                    Resolved: <span className="text-foreground">{testResult.resolved_ip}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {!testResult.ok && testResult.stage === "private-network" && testResult.detail && (
                                        <div className="mt-1 text-[11px] text-blue-200/80">
                                            {testResult.detail}
                                        </div>
                                    )}
                                    {!testResult.ok && testResult.stage !== "private-network" && testResult.detail && (
                                        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                                            {testResult.detail}
                                            <div className="mt-1 text-muted-foreground">
                                                Check IP address, port, and credentials.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-border bg-surface px-5 py-3">
                    <button
                        type="button"
                        onClick={test}
                        disabled={testing || !host || !share}
                        data-testid="storage-test-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40 disabled:opacity-50"
                    >
                        {testing ? <Loader2 size={13} className="animate-spin" /> : <Server size={13} />}
                        {testing ? "Testing…" : "Test Connection"}
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            disabled={busy || !host || !share}
                            data-testid="storage-save-btn"
                            className="rounded-md bg-gradient-to-r from-[#00C2FF] to-[#5b5ef0] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-md hover:shadow-lg disabled:opacity-60"
                        >
                            {isEdit ? (busy ? "Saving…" : "Save Changes") : (busy ? "Connecting…" : "Connect Storage")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

